import { makeStyles } from "@fluentui/react-components";
import { primer } from "./primerDarkTheme";

export const useEmptyStateLandingStyles = makeStyles({
    /* ---- Getting Started card ---- */
    card: {
        backgroundColor: primer.surface,
        border: `1px solid ${primer.border}`,
        borderRadius: "6px",
        display: "flex",
        flexDirection: "column",
        width: "100%",
    },
    cardHeader: {
        display: "flex",
        alignItems: "center",
        padding: "12px 16px",
        overflow: "hidden",
    },
    cardHeaderText: {
        fontSize: "16px",
        fontWeight: 600,
        color: primer.fg,
        whiteSpace: "nowrap",
        margin: 0,
    },
    cardDivider: {
        height: "1px",
        width: "100%",
        backgroundColor: primer.border,
        flexShrink: 0,
    },
    cardBody: {
        display: "flex",
        flexDirection: "column",
        gap: "24px",
        padding: "24px 32px 32px",
    },

    /* ---- Hero subtitle ---- */
    heroSubtitle: {
        fontSize: "24px",
        fontWeight: 600,
        lineHeight: "32px",
        color: "#ffffff",
        margin: 0,
    },

    /* ---- Steps ---- */
    step: {
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        width: "100%",
    },
    stepTitle: {
        fontSize: "20px",
        fontWeight: 600,
        lineHeight: "28px",
        color: "#d6d6d6",
        margin: 0,
    },
    stepBody: {
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        padding: "0 8px",
        width: "100%",
    },
    stepDescription: {
        fontSize: "14px",
        lineHeight: "20px",
        color: "#adadad",
        margin: 0,
    },
    itemList: {
        margin: 0,
        padding: "0 0 0 8px",
        display: "block",
        listStyleType: "disc",
        fontSize: "14px",
        lineHeight: "20px",
        color: "#adadad",
    },
    itemEntry: {
        marginLeft: "21px",
        marginBottom: 0,
    },

    /* ---- Code block ---- */
    codeBlock: {
        backgroundColor: "#151720",
        border: `1px solid ${primer.border}`,
        borderRadius: "12px",
        padding: "12px 14px",
        fontFamily: "'Menlo', 'SF Mono', SFMono-Regular, Consolas, 'Liberation Mono', monospace",
        fontSize: "12px",
        lineHeight: "19px",
        color: primer.fg,
        overflowX: "auto",
        whiteSpace: "pre-wrap",
        margin: 0,
        display: "flex",
        alignItems: "flex-start",
        width: "100%",
        boxSizing: "border-box",
    },
    codeText: {
        flex: "1 0 0",
        minWidth: 0,
        margin: 0,
        padding: 0,
        fontFamily: "inherit",
        fontSize: "inherit",
        lineHeight: "inherit",
        color: "inherit",
        whiteSpace: "pre-wrap",
    },
    copyBtn: {
        flexShrink: 0,
        width: "20px",
        height: "20px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "none",
        border: "none",
        cursor: "pointer",
        color: primer.fgMuted,
        borderRadius: "4px",
        ":hover": {
            color: primer.fg,
            backgroundColor: "rgba(255,255,255,0.1)",
        },
    },
    stepLink: {
        fontSize: "14px",
        color: "#479ef5",
        textDecoration: "underline",
        textDecorationColor: "#479ef5",
        ":hover": { color: "#58a6ff" },
    },

    /* ---- Syntax colors ---- */
    syntaxGreen: {
        color: "#00ac66",
    },
    syntaxPurple: {
        color: "#9580e5",
    },
    syntaxBlue: {
        color: "#5386d3",
    },
    syntaxComment: {
        color: "#adadad",
    },

    /* ---- Inline code ---- */
    inlineCode: {
        fontFamily: "'Menlo', 'SF Mono', SFMono-Regular, Consolas, 'Liberation Mono', monospace",
        fontSize: "12px",
        backgroundColor: "rgba(255,255,255,0.06)",
        padding: "2px 6px",
        borderRadius: "4px",
        color: "#d6d6d6",
    },

    /* ---- Commands table ---- */
    table: {
        width: "100%",
        borderCollapse: "collapse" as const,
        border: `1px solid ${primer.border}`,
        borderRadius: "6px",
        overflow: "hidden",
        fontSize: "14px",
        lineHeight: "20px",
    },
    th: {
        textAlign: "left" as const,
        padding: "10px 14px",
        color: "#d6d6d6",
        fontWeight: 600,
        backgroundColor: "rgba(255,255,255,0.04)",
        borderBottom: `1px solid ${primer.border}`,
    },
    td: {
        padding: "10px 14px",
        color: "#adadad",
        borderBottom: `1px solid ${primer.border}`,
        verticalAlign: "top",
    },

    /* ---- Keep going card ---- */
    keepGoingCard: {
        backgroundColor: primer.surface,
        border: `1px solid ${primer.border}`,
        borderRadius: "6px",
        padding: "16px",
        display: "flex",
        flexDirection: "column" as const,
        gap: "8px",
    },
    keepGoingTitle: {
        fontSize: "16px",
        fontWeight: 600,
        color: "#d6d6d6",
        margin: 0,
    },
    keepGoingDesc: {
        fontSize: "13px",
        lineHeight: "18px",
        color: "#adadad",
        margin: 0,
    },
    keepGoingLinks: {
        display: "flex",
        gap: "12px",
        marginTop: "4px",
    },

    /* ---- Right sidebar ---- */
    sidebar: {
        display: "flex",
        flexDirection: "column",
        gap: "24px",
        flexShrink: 0,
        width: "320px",
        padding: "20px 24px 24px 0",
    },
    sidebarCard: {
        backgroundColor: primer.surface,
        border: `1px solid ${primer.border}`,
        borderRadius: "6px",
        padding: "16px",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        overflow: "hidden",
    },
    sidebarTitle: {
        fontSize: "14px",
        fontWeight: 600,
        color: primer.fg,
        margin: 0,
        whiteSpace: "nowrap",
    },
    linkGroup: {
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        padding: "4px 8px",
        borderRadius: "6px",
    },
    linkItem: {
        fontSize: "13px",
        color: primer.blue,
        textDecoration: "none",
        display: "block",
        width: "100%",
        ":hover": {
            textDecoration: "underline",
        },
    },
});
